import useRevenueCat from "@utils/revenuecat";
import { USER_ME_QUERY_KEY } from "@hooks/trpc/useMe";
import { captureException } from "@utils/captureException";
import { useData } from "../runtime/provider";
import {
  getSubscribeFailureToast,
  type RevenueCatSubscribeResult,
} from "@utils/revenuecatErrors";
import { toast } from "sonner-native";
import type { PurchasesPackage } from "react-native-purchases";

export type SubscribeResult =
  | { success: true }
  | ({
      success: false;
    } & Pick<
      RevenueCatSubscribeResult,
      "error" | "errorCode" | "pending" | "userCancelled"
    >);

export const useSubscribe = () => {
  const { queryClient, offlineAccess } = useData();
  const {
    subscribe: sub,
    monthlyOffering: monthlyOfferingQuery,
    annualOffering: annualOfferingQuery,
    product,
    annualProduct,
  } = useRevenueCat({ surface: "paywall", reportOfferingErrors: false });
  const monthlyOffering = monthlyOfferingQuery.data;
  const annualOffering = annualOfferingQuery.data;
  const subscribe = async (
    offering: PurchasesPackage | null | undefined,
  ): Promise<SubscribeResult | undefined> => {
    if (!offering) return;

    try {
      captureException("Paywall purchase started", {
        channel: "breadcrumb",
        area: "paywall",
        operation: "purchase-started",
        tags: {
          revenuecat_surface: "paywall",
          paywall_phase: "purchase",
        },
      });

      const result = await sub(offering);

      if (result.success) {
        // RevenueCat's webhook is the plan writer. It normally lands before
        // the user can reach a download, but retry the generic access endpoint
        // briefly so this session can receive its lease without a restart.
        await offlineAccess
          .refreshOfflineAccess({ retryInvalid: true })
          .catch((error) => {
            captureException(error, {
              channel: "warning",
              area: "subscription",
              operation: "refresh-offline-access-after-purchase",
            });
          });
        await queryClient.invalidateQueries({ queryKey: USER_ME_QUERY_KEY });

        captureException("Paywall purchase succeeded", {
          channel: "breadcrumb",
          area: "paywall",
          operation: "purchase-succeeded",
          tags: {
            revenuecat_surface: "paywall",
            paywall_phase: "purchase",
          },
        });

        setTimeout(() => {
          toast.success("Subscribed successfully!", {
            description: "+20 Gems",
          });
        }, 300);

        return { success: true };
      }

      const failureToast = getSubscribeFailureToast(result);
      if (failureToast) {
        toast[failureToast.type](failureToast.header, {
          description: failureToast.message,
        });
      }

      captureException("Paywall purchase failed", {
        channel: "breadcrumb",
        area: "paywall",
        operation: "purchase-failed",
        tags: {
          revenuecat_surface: "paywall",
          paywall_phase: "purchase",
          ...(result.errorCode ? { rc_error_code: result.errorCode } : {}),
          ...(result.pending ? { rc_payment_pending: "true" } : {}),
          ...(result.userCancelled ? { rc_user_cancelled: "true" } : {}),
        },
        extra: {
          revenueCat: {
            message: result.error,
            errorCode: result.errorCode,
            pending: result.pending ?? false,
            userCancelled: result.userCancelled ?? false,
          },
        },
      });

      return {
        success: false,
        error: result.error,
        errorCode: result.errorCode,
        pending: result.pending,
        userCancelled: result.userCancelled,
      };
    } catch (error) {
      captureException(error, {
        channel: "error",
        area: "paywall",
        fingerprint: ["paywall", "update-user"],
        operation: "subscribe-update-user",
        tags: {
          revenuecat_surface: "paywall",
          paywall_phase: "user_update",
        },
      });
      toast.error("Subscription failed", {
        description: "Please try again",
      });

      return { success: false, error: String(error) };
    }
  };

  return {
    subscribe,
    monthlyOffering,
    monthlyOfferingQuery,
    annualOffering,
    annualOfferingQuery,
    product,
    annualProduct,
  };
};
